import { useEffect, useRef } from 'react';

import { bindAll } from 'bind-event-listener';

import { fg } from '@atlaskit/platform-feature-flags';
import traceUFOInteraction from '@atlaskit/react-ufo/trace-interaction';
import traceUFOPress from '@atlaskit/react-ufo/trace-press';

import { QUICK_FIND_CLOSE_INTERACTION } from '../../../common/constants/quick-find';
import { serve3PBulkConnectWithoutExposure } from '../../../common/utils/experiments';
import {
	useIsBulkConnectModalOpen,
	useIsFeedbackModalOpen,
	useIsHoverCardOpen,
	useQuickFindActions,
	useQuickFindOpen,
} from '../../../controllers/store/quick-find';

export const useCloseManager = ({
	dialogRef,
	inputBoxRef,
}: {
	dialogRef: React.RefObject<HTMLDivElement>;
	inputBoxRef: React.RefObject<HTMLDivElement>;
}) => {
	const mouseDownInside = useRef(false);
	const isHoverCardOpen = useIsHoverCardOpen();

	// Hover card not taking ref as input, we can only check with selectors.
	// Check if click is happening within smart link container or related links modal children,
	// if it is, we don't close the quick find
	const isClickInSmartLinkContainer = (targetNode: Node | null): boolean => {
		if (!targetNode) {
			return false;
		}

		const smartLinkContainer = document.querySelector(
			'[data-testid="hover-card"][aria-label="quick-find-smart-hover-card"]',
		);
		const stateManagementContainer = document.querySelector(
			'[data-testid="state-metadata-element-item-group"]',
		);
		const relatedLinksModal = document.querySelector('[data-testid="related-links-modal"]');
		const relatedLinkModalBlanket = document.querySelector(
			'[data-testid="related-links-modal--blanket"]',
		);

		const isInSmartLink = smartLinkContainer ? smartLinkContainer.contains(targetNode) : false;
		const isInRelatedLinksModal = relatedLinksModal
			? relatedLinksModal.contains(targetNode)
			: false;
		const isInRelatedLinkModalBlanket = relatedLinkModalBlanket
			? relatedLinkModalBlanket.contains(targetNode)
			: false;
		const isInStateManagementContainer = stateManagementContainer
			? stateManagementContainer.contains(targetNode)
			: false;

		// When hover card visibility change is enabled, we don't need this to check for click in hover card.
		// This function will only check for Related Links Modal click.
		const isClickedInHoverCard = isInSmartLink || isInStateManagementContainer;
		const clickInHoverCard = !fg('hover-card-on-visibility-change-callback')
			? isClickedInHoverCard
			: false;

		return clickInHoverCard || isInRelatedLinksModal || isInRelatedLinkModalBlanket;
	};

	const quickFindOpen = useQuickFindOpen();

	const [isFeedbackModalOpen] = useIsFeedbackModalOpen();
	const [isBulkConnectModalOpen] = useIsBulkConnectModalOpen();
	const { setQuickFindOpen } = useQuickFindActions();

	useEffect(() => {
		const onMouseDown = (event: MouseEvent | React.MouseEvent) => {
			mouseDownInside.current = false;

			const { target } = event;
			const targetNode = target as Node | null;

			const isClickOnPopup = dialogRef.current && dialogRef.current.contains(targetNode);
			const isClickOnTrigger = inputBoxRef.current && inputBoxRef.current.contains(targetNode);

			if (isClickOnPopup || isClickOnTrigger) {
				mouseDownInside.current = true;
			}
		};

		const onClick = (event: MouseEvent | React.MouseEvent) => {
			const { target } = event;
			const targetNode = target as Node | null;

			if (isFeedbackModalOpen) {
				return;
			}

			if (isBulkConnectModalOpen && serve3PBulkConnectWithoutExposure()) {
				return;
			}

			// We need to keep isClickInSmartLinkContainer around as the visibility change of the hover card is not fully rolled out
			if (isHoverCardOpen || isClickInSmartLinkContainer(targetNode)) {
				return;
			}

			const isClickOnPopup = dialogRef.current && dialogRef.current.contains(targetNode);
			const isClickOnTrigger = inputBoxRef.current && inputBoxRef.current.contains(targetNode);

			if (!isClickOnPopup && !isClickOnTrigger && !mouseDownInside.current) {
				setQuickFindOpen(false);

				if (fg('quick_find_metrics_using_ufo_react')) {
					if (quickFindOpen) {
						traceUFOInteraction(QUICK_FIND_CLOSE_INTERACTION, event as React.MouseEvent);
					}
				}
			}
		};

		const onKeyDown = (event: KeyboardEvent | React.KeyboardEvent) => {
			const { key } = event;
			if (key === 'Escape' || key === 'Esc') {
				setQuickFindOpen(false);

				if (fg('quick_find_metrics_using_ufo_react')) {
					if (quickFindOpen) {
						traceUFOInteraction(QUICK_FIND_CLOSE_INTERACTION, event as React.KeyboardEvent);
					}
				}
			}
		};

		const onBlur = (e: FocusEvent) => {
			const activeElement = document.activeElement;
			const activeElementIsIFrame = activeElement instanceof HTMLIFrameElement;

			// Close Quick Find if the new active element is an iframe, we need
			// to do this check since click events are not fired on iframes
			if (activeElementIsIFrame) {
				setQuickFindOpen(false);

				if (fg('quick_find_metrics_using_ufo_react')) {
					if (quickFindOpen) {
						traceUFOPress(QUICK_FIND_CLOSE_INTERACTION, performance.now());
					}
				}
			}
		};

		const unbind = bindAll(window, [
			{
				type: 'mousedown',
				listener: onMouseDown,
				options: { capture: false },
			},
			{
				type: 'click',
				listener: onClick,
				options: {
					capture: true, // Closes the dialog before SPA transitions
				},
			},
			{
				type: 'keydown',
				listener: onKeyDown,
			},
			{
				type: 'blur',
				listener: onBlur,
			},
		]);

		return () => {
			unbind();
		};
	}, [
		setQuickFindOpen,
		isFeedbackModalOpen,
		isBulkConnectModalOpen,
		quickFindOpen,
		dialogRef,
		inputBoxRef,
		isHoverCardOpen,
	]);
};
